import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'

interface Release {
  version:  string
  date:     string
  type:     'major' | 'minor' | 'patch'
  title:    string
  summary:  string
  changes:  { cat: string; items: string[] }[]
}

const RELEASES: Release[] = [
  {
    version: '2.0.0',
    date:    'March 2026',
    type:    'major',
    title:   'Live Data Analysis — No Screenshot Required',
    summary: 'The biggest upgrade since launch. Instead of uploading a chart screenshot, Pro and Super users can now type any ticker and let Polywally fetch real OHLCV data and run the full 14-module ICT analysis on actual price numbers.',
    changes: [
      {
        cat: 'New Feature — Ticker Input Analysis',
        items: [
          'Type any US ticker (stocks, ETFs) and select a timeframe — no screenshot needed',
          'Pro tier: Daily and Weekly OHLCV analysis ',
          'Super tier: All timeframes including 1m, 5m, 15m, 1H, 4H — full intraday support',
          'Super tier: Auto MTF mode fetches a second timeframe automatically for confluence (e.g. 15m + 1H)',
          'Analysis is grounded in real OHLCV bars — entry zones, stops, and targets come from actual price levels',
          'Live data badge shows bar count, source, and timeframe used',
          'Screenshot upload remains available as an alternative input method',
        ],
      },
      {
        cat: 'Infrastructure — Live Market Data',
        items: [
          'Live market data powers all platform features',
          'Real-time quotes replace Finnhub on watchlist, scanner, and live quote widget',
          'OHLCV bars available at all timeframes: 1m through Weekly',
          'Unlimited API calls — no rate limits or per-user data costs',
          'Consistent, exchange-consolidated data across all features',
        ],
      },
      {
        cat: 'Super Tier — Intraday + MTF',
        items: [
          'Intraday timeframes (1m, 5m, 15m, 1H, 4H) locked to Super tier for ticker analysis',
          'Auto MTF toggle fetches paired higher timeframe for confluence automatically',
          'MTF pairs: 1m→15m, 5m→1H, 15m→1H, 1H→1D, 4H→1D, 1D→1W',
          'Both timeframes analyzed in a single AI call — true institutional-grade MTF ICT analysis',
          'Live quote injected alongside OHLCV for maximum context',
        ],
      },
      {
        cat: 'Bug Fixes',
        items: [
          'Fixed NaN% displayed on Dashboard analysis usage for free tier users',
          'Fixed "NaN% used" on Analyze page eyebrow for free users',
          'Free tier watchlist and scanner now show realistic preview examples',
          'Acceptable use policy updated to match industry standard language',
        ],
      },
    ],
  },
  {
    version: '1.5.0',
    date:    'February 2026',
    type:    'minor',
    title:   'Trade Journal, Watchlist, Scanner & Risk Calculator',
    summary: 'Four new features added in one release — all running on free infrastructure with no per-use costs.',
    changes: [
      {
        cat: 'New Features',
        items: [
          'Trade Journal — log entries, exits, P&L, setup grades, emotions, and lessons',
          'Watchlist — track tickers with live quotes refreshed every 60 seconds',
          'Price Alerts — set above/below thresholds on any watchlist ticker',
          'Setup Scanner — scan up to 20 tickers for ICT key level proximity (FVGs, OBs, PDH/PDL)',
          'Risk Calculator — position size calculator inline with the scanner',
          'Free tier preview mode on Watchlist and Scanner with realistic demo data',
        ],
      },
    ],
  },
  {
    version: '1.4.0',
    date:    'February 2026',
    type:    'minor',
    title:   'AI Model Rebrand + Privacy Rewrite',
    summary: 'Removed AI provider names from user-facing UI. Updated Terms and Privacy pages.',
    changes: [
      {
        cat: 'Changes',
        items: [
          'AI tier labels changed from "Gemini / Claude Haiku" to "Pro AI / Super AI"',
          'Terms rewritten in natural prose — no more bullet lists or em-dashes',
          'Acceptable use policy updated to industry-standard language',
          'Privacy policy streamlined to 9 minimal sections',
        ],
      },
    ],
  },
  {
    version: '1.3.0',
    date:    'January 2026',
    type:    'minor',
    title:   'Options Analysis + Live Market Data',
    summary: 'Screenshot-based options chain analysis for Super users. Live market data injected into Super AI analysis.',
    changes: [
      {
        cat: 'New Features',
        items: [
          'Options chain analysis from screenshots (IV rank, max pain, gamma walls, flow)',
          'Auto-detection of ticker and expiry from options screenshots',
          'Live market data (Finnhub) injected into Super AI prompts',
          'Options analysis history tab',
          'Trade visualization chart with entry/stop/targets and R:R display',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date:    'January 2026',
    type:    'minor',
    title:   'Paper Trading + News Terminal',
    summary: 'Full paper trading UI via Alpaca. Finnhub news ticker with four feed categories.',
    changes: [
      {
        cat: 'New Features',
        items: [
          'Paper trading dashboard with Portfolio and Orders tabs',
          'Bracket order modal with entry, stop, and take-profit',
          'Finnhub news ticker — compact bar and full terminal feed',
          'Four news categories: General, Forex, Crypto, M&A',
          'DCA compound growth calculator',
        ],
      },
    ],
  },
  {
    version: '1.0.0',
    date:    'December 2025',
    type:    'major',
    title:   'Launch',
    summary: 'Polywally goes live with 14-module ICT chart analysis, tiered AI models, and screenshot-based analysis.',
    changes: [
      {
        cat: 'Initial Release',
        items: [
          '14-module ICT/SMC analysis: market structure, OBs, FVGs, liquidity, BOS/CHoCH, OTE, premium/discount, and more',
          'Screenshot upload with drag & drop, paste, and multi-file support',
          'Timeframe selector with auto-detection from chart images',
          'Pro tier: Gemini 1.5 Flash analysis, unlimited monthly analyses',
          'Super tier: Claude Haiku analysis with enhanced prompting',
          'Free tier with realistic preview mode — see what you get before upgrading',
          'Analysis history with expandable detail view',
          'Alpaca paper trading account connection',
          'Mobile-responsive layout',
        ],
      },
    ],
  },
]

const TYPE_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  major: { bg: 'rgba(133,196,124,.12)', color: 'var(--cash)',  label: 'MAJOR' },
  minor: { bg: 'rgba(212,168,67,.08)',  color: 'var(--amber)', label: 'MINOR' },
  patch: { bg: 'rgba(100,100,120,.1)',  color: 'var(--muted)', label: 'PATCH' },
}

export default function Changelog() {
  const { user }  = useAuthStore()
  const navigate  = useNavigate()
  const tier      = user?.tier ?? 'free'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 780, margin: '0 auto' }}>

      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Changelog</h1>
        <div className="eyebrow">What's new in Polywally</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {RELEASES.map((r, i) => {
          const ts = TYPE_STYLE[r.type]
          const isLatest = i === 0
          return (
            <div key={r.version} style={{ display: 'flex', gap: 24, position: 'relative' }}>

              {/* Timeline spine */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 16 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: isLatest ? 'var(--cash)' : 'var(--edge)', border: `2px solid ${isLatest ? 'var(--cash)' : 'var(--muted)'}`, boxShadow: isLatest ? '0 0 10px var(--cash)' : 'none', flexShrink: 0, marginTop: 4 }} />
                {i < RELEASES.length - 1 && (
                  <div style={{ flex: 1, width: 1, background: 'var(--edge)', marginTop: 6 }} />
                )}
              </div>

              {/* Release card */}
              <div style={{ flex: 1, paddingBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.1rem', color: 'var(--bill)' }}>v{r.version}</div>
                  <div style={{ padding: '3px 9px', background: ts.bg, color: ts.color, fontFamily: 'Syne Mono', fontSize: '.56rem', fontWeight: 700, letterSpacing: '.12em', borderRadius: 2 }}>
                    {ts.label}
                  </div>
                  {isLatest && (
                    <div style={{ padding: '3px 9px', background: 'rgba(133,196,124,.12)', color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.56rem', fontWeight: 700, letterSpacing: '.12em', borderRadius: 2, border: '1px solid var(--cash-dim)' }}>
                      LATEST
                    </div>
                  )}
                  <div style={{ marginLeft: 'auto', fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>{r.date}</div>
                </div>

                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.95rem', color: 'var(--text)', marginBottom: 8 }}>{r.title}</div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: 16 }}>{r.summary}</div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {r.changes.map(section => (
                    <div key={section.cat}>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--cash)', marginBottom: 8, opacity: .8 }}>{section.cat}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {section.items.map((item, j) => (
                          <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                            <span style={{ color: 'var(--cash)', flexShrink: 0, marginTop: 1 }}>→</span>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA for latest major */}
                {isLatest && tier !== 'super' && (
                  <div style={{ marginTop: 18, padding: '14px 16px', background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.78rem', color: 'var(--cash)', marginBottom: 3 }}>
                        {tier === 'free' ? 'Try the new ticker analysis' : 'Unlock intraday + MTF analysis'}
                      </div>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                        {tier === 'free'
                          ? 'Pro gives you Daily/Weekly analysis on any ticker with no screenshot needed'
                          : 'Super unlocks 1m–4H intraday, auto MTF confluence, and Super AI'}
                      </div>
                    </div>
                    <button onClick={() => navigate('/pricing')} style={{ padding: '9px 20px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.75rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {tier === 'free' ? 'Get Pro — $9.99/mo →' : 'Get Super — $99.99/mo →'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
